<template>
  <div class="player-update-teams">
    <!-- Button -->
    <el-button
      v-if="hasPermission"
      type="primary"
      @click="handleUpdateTeams()"
    > 
      {{i18n('entities.player.updateTeams.action')}}
    </el-button>

    <el-dialog
      v-if="dialogVisible"
      :before-close="back"
      :visible.sync="dialogVisible"
      width="90%"
      class="player-update-teams__modal "
    >
      <!-- Header -->
      <span slot="title" class="player-update-teams__modal__header">
        <app-i18n
          class="player-update-teams__modal__header__title"
          code="entities.player.updateTeams.title"
        />
      </span>

      <!-- Card -->
      <app-view-card
        class="player-update-teams__modal__player-card"
        v-if="playerCardParams"
        :title="playerCardParams.title" 
        :fieldsValuesArray="playerCardParams.fieldsValuesArray"
        :icon="playerCardParams.icon"
      />

      <!-- Team transfer -->
      <div
        v-loading="loading"
      >
        <app-team-list-transfer
          :key="teamListTransferKey"
          :subpropertyId="subpropertyId"
          :listTeamPlayerInitial="listTeamPlayer"
          :listTeamPlayerResult.sync="listTeamPlayerResult"
          :playerFullname="playerFullname"
        ></app-team-list-transfer>
      </div>
      
      <!-- Footer -->
      <div slot="footer" class="player-update-teams__modal__footer">
        <el-button
          :disabled="disableSubmitDialog"
          type="primary"
          @click="doSubmit()"
        >{{i18n('common.update')}}</el-button>
      </div>
    </el-dialog>
  </div>
</template>


<script>
import { i18n } from '@shared/i18n';
import { PlayerModel } from '@modules/player/player-model';
import { mapGetters, mapActions } from 'vuex';
import TeamListTransfer from '@modules/team/components/team-list-transfer.vue';

export default {
  name: 'player-update-teams',
  props: {
    playerData: {
      type: Object,
      required: true,
    },

    subpropertyId: {
      type: String,
      required: true,
    },

    onFinishSubmitFn: {
      type: Function,
      required: true,
    },

    hasPermission: {
      type: Boolean,
      required:true
    }
  },

  data() {
    return {
      dialogVisible: false,
      statusInitial: null,
      statusSelected: null,
      teamListTransferKey: 0,
      listTeamPlayerResult: []
    };
  },

  created() {
    const { status } = this.playerData;
    this.statusInitial = status;
    this.statusSelected = status;
    this.teamListTransferKey++;
  },

  computed: {
    ...mapGetters({
      loading: 'player/updateTeams/loading',
      listTeamPlayer: 'player/updateTeams/listTeamPlayer',
      dateFormat: 'auth/manage/dateFormat',
      timeFormat: 'auth/manage/timeFormat',
    }),

    isDisableStatusAction() {
      return (
        process.env
          .VUE_APP_DISABLE_INSCRIPTION_STATUSCHANGE ===
        'true'
      );
    },

    isDisabledStatus() {
      const isAccepted =
        this.statusSelected === 'approved' ||
        this.statusSelected === 'onTryout';

      return this.isDisableStatusAction || isAccepted;
    },

    statusSupportedList() {
      const statusSuported = [
        'pendingApproval',
        'approved',
        'onTryout',
        'pendingPayment',
      ];

      return this.fields.status.options.filter(({ id }) => {
        return statusSuported.some(
          (status) => status === id,
        );
      });
    },
    
    fields() {
      const { dateFormat, timeFormat} = this;
      return new PlayerModel({
        dateFormat,
        timeFormat
      }).fields();
    },

    programId() {
      return this.playerData.program.id;
    },

    subprogramId() {
      return this.playerData.subprogram.id;
    },

    playerFullname() {
      const { firstName, lastName } = this.playerData;
      return `${firstName} ${lastName}`
    },

    disableSubmitDialog() {
      return this.listTeamPlayerResult.length == 0;
    },

    playerCardParams() {
      const { firstName, lastName } = this.playerData;
      const title = `${firstName} ${lastName}`;
      
      return {
        icon: 'icon-player',
        title,
        fieldsValuesArray : [
          {
            fieldI18n: 'entities.player.fields.birthdate',
            value: this.presenter(this.playerData, 'birthdate'),
            col: { lg: 24, md: 24, sm: 24 } 
          },
          {
            fieldI18n: 'entities.player.fields.gender',
            value: this.presenter(this.playerData, 'gender'),
            col: { lg: 24, md: 24, sm: 24 } 
          },
        ]
      };
    },
  },

  methods: {
    ...mapActions({
      doReset: 'player/updateTeams/doReset',
      doGetTeams: 'player/updateTeams/doGetTeams',
      doUpdateTeams: 'player/updateTeams/doUpdateTeams',
    }),

    presenter(row, fieldName) {
      const { dateFormat, timeFormat } = this;
      return new PlayerModel({ dateFormat, timeFormat }).presenter(row, fieldName);
    },

    handleUpdateTeams() {
      // Load teams
      const { id } = this.playerData;
      
      this.doGetTeams({ id , subpropertyId:  this.subpropertyId});

      // Show dialog
      this.dialogVisible = true;
    },

    async doSubmit() {
      this.dialogVisible = false;
      const { id } = this.playerData;

      this.doUpdateTeams({
        id,
        subpropertyId: this.subpropertyId,
        teams: this.listTeamPlayerResult.map((e) => ({ 
          id: e.id, 
          program: e.program.id, 
          subprogram: e.subprogram.id, 
        })),
      }).then(() => {
        this.onFinishSubmitFn();
        this.back();
      });
    },

    back() {
      // Hide dialog
      this.dialogVisible = false;

      // Reset status selected
      this.statusSelected = this.statusInitial;

      // Reset team list player
      this.listTeamPlayerResult = [];

      // Reset store
      this.doReset();

      this.teamListTransferKey++;
    },

    i18n(code) {
      return i18n(code);
    },
  },

  watch: {
    listTeamPlayer(newValue, oldValue) {
      this.teamListTransferKey++;
    }
  },
  
  components: {
    [TeamListTransfer.name]: TeamListTransfer,
  },
};
</script>

<style lang="scss" scoped>
.player-update-teams {

  &__modal {
    display: grid;
    &__header {
      &__title {
        color: #333333;
        font-size: 20px;
        font-weight: bold;
        text-transform: uppercase;
        padding-bottom: 32px;
      }
    }

    &__player-card {
      margin: 0 10% 0 10%;
    }

    &__player-card strong {
      color: #404040;
      font-weight: normal;
    }

    &__footer {
      display: flex;
      justify-content: center;
    }
  }
}
</style>